import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const STATS = [
  ["$150,000 / yr", "GRANT VALUE"],
  ["G7 Only", "UNIVERSITY STANDARD"],
  ["3 Yrs Max.", "PROGRAM LENGTH"],
  ["Priority Interview", "ON COMPLETION"],
];

const CREDS = [
  ["CFA Charter", "signals applied investment judgment alongside academic research depth."],
  ["Master's in Finance / Financial Engineering", "particularly where the PhD is in a more theoretical discipline (mathematics, physics, statistics, computer science)."],
  ["FRM", "the Financial Risk Manager designation is a plus for candidates leaning toward risk or portfolio construction roles."],
  ["Relevant work experience", "prior internships or roles in quantitative research, trading, or data science are a meaningful differentiator, though not required to apply."],
  ["Technical fluency", "demonstrated proficiency in Python, C++, or similar, and published or working research papers, strengthen any application."],
];

const STEPS = [
  "Submit proof of PhD admission (or current enrollment) and your CV.",
  "A one-page statement of research interest and career goals.",
  "Shortlisted candidates meet with our research and talent teams.",
  "The Fellowship is confirmed before the academic term begins.",
];

export default function Fellowship() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", country: "",
    university: "", degree_program: "", enrollment_status: "admitted",
    statement: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("cv_pdf", file);
      await api.post("/fellowship/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Application submitted.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="container-tight py-20 max-w-2xl">
        <div className="kicker mb-3">Received</div>
        <h1 className="text-5xl mb-6">Thank you.</h1>
        <p className="text-muted-foreground leading-relaxed">
          Your Piplight Fellowship application has been recorded. Our research and talent teams will
          be in touch regarding next steps.
        </p>
      </div>
    );
  }

  return (
    <div className="container-tight py-16 max-w-5xl space-y-12">
      <div>
        <div className="kicker mb-3">Graduate Research Funding</div>
        <h1 className="text-5xl md:text-6xl mb-4 leading-[1.05]">The Piplight Fellowship</h1>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          A US$150,000 annual grant, over three years, for PhD candidates in Quantitative Investment & Trading, with a
          direct pathway to a role at our firm.
        </p>
        <div className="grid md:grid-cols-4 gap-4 mt-10">
          {STATS.map(([v, k]) => (
            <div key={k} className="stat-tile"><div className="font-mono text-lg gold">{v}</div><div className="kicker text-[10px] mt-2">{k}</div></div>
          ))}
        </div>
      </div>

      <section className="border-t border-border pt-12">
        <div className="kicker mb-2">Program Overview</div>
        <h2 className="text-3xl md:text-4xl mb-4">One Fellow, one award cycle.</h2>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
          Piplight Capital funds one PhD candidate per award cycle with a US$150,000 annual grant,
          renewable for a maximum of three years, to pursue a PhD in
          Quantitative Analysis & Trading (or a closely related quantitative finance discipline) at
          any G7-accredited university. On successful completion, the Fellow is invited to apply for
          a position at any of our global offices, with priority interview status ahead of the
          general applicant pool.
        </p>
      </section>

      <section className="border-t border-border pt-12">
        <div className="kicker mb-2">Eligibility & Terms</div>
        <ul className="space-y-3 text-sm text-muted-foreground leading-relaxed max-w-3xl">
          <li><span className="text-foreground">Field of study —</span> PhD in Quantitative Analysis & Trading, Financial Engineering, Financial Mathematics, or a closely related quantitative discipline.</li>
          <li><span className="text-foreground">University standard —</span> must hold, or have accepted, admission to an accredited university in a G7 nation (Canada, France, Germany, Italy, Japan, UK or US).</li>
          <li><span className="text-foreground">Program length —</span> minimum 3 years, full-time, leading to a conferred doctorate.</li>
          <li><span className="text-foreground">Renewal —</span> grant renews annually contingent on satisfactory academic standing, for a maximum of three years total.</li>
          <li><span className="text-foreground">Post-graduation pathway —</span> successful Fellows may apply to any Piplight Capital location; completion of the Fellowship guarantees a priority interview, not an offer of employment.</li>
        </ul>
      </section>

      <section className="border-t border-border pt-12">
        <div className="kicker mb-2">Credentials That Also Strengthen a Candidacy</div>
        <p className="text-sm text-muted-foreground mb-4 max-w-3xl leading-relaxed">
          A PhD is the core requirement, but the following are viewed favorably at both application and post-Fellowship hiring stages:
        </p>
        <ul className="space-y-2 text-sm">
          {CREDS.map(([t, d]) => (
            <li key={t}><span className="text-foreground">{t}:</span> <span className="text-muted-foreground">{d}</span></li>
          ))}
        </ul>
      </section>

      <section className="border-t border-border pt-12">
        <div className="kicker mb-2">How To Apply</div>
        <ol className="space-y-2 text-sm text-muted-foreground list-none max-w-3xl">
          {STEPS.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="font-mono gold w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-t border-border pt-12">
        <div className="kicker mb-2">Fellowship Application</div>
        <h2 className="text-3xl mb-6">Apply Now</h2>
        <form onSubmit={submit} className="surface p-6 grid md:grid-cols-2 gap-4">
          <Field label="Full Name"><input required value={f.full_name} onChange={(e) => setF({...f, full_name: e.target.value})} className="fld" data-testid="fel-name" /></Field>
          <Field label="Email"><input required type="email" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} className="fld" data-testid="fel-email" /></Field>
          <Field label="Phone"><input value={f.phone} onChange={(e) => setF({...f, phone: e.target.value})} className="fld" data-testid="fel-phone" /></Field>
          <Field label="Country"><input value={f.country} onChange={(e) => setF({...f, country: e.target.value})} className="fld" data-testid="fel-country" /></Field>
          <Field label="University (must be G7-accredited)"><input required value={f.university} onChange={(e) => setF({...f, university: e.target.value})} className="fld" data-testid="fel-uni" /></Field>
          <Field label="PhD Program / Discipline"><input required value={f.degree_program} onChange={(e) => setF({...f, degree_program: e.target.value})} className="fld" data-testid="fel-program" /></Field>
          <Field label="Enrollment Status">
            <select value={f.enrollment_status} onChange={(e) => setF({...f, enrollment_status: e.target.value})} className="fld" data-testid="fel-status">
              <option value="admitted">Admitted (not yet enrolled)</option>
              <option value="enrolled">Currently enrolled</option>
              <option value="applying">Applying this cycle</option>
            </select>
          </Field>
          <Field label="CV & Statement (PDF, up to 8 MB)">
            <input type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="fld file:mr-3 file:bg-secondary file:border-0 file:text-foreground file:px-3 file:py-1"
              data-testid="fel-pdf" />
          </Field>
          <div className="md:col-span-2">
            <Field label="One-page statement of research interest & career goals">
              <textarea required rows={5} value={f.statement} onChange={(e) => setF({...f, statement: e.target.value})} className="fld resize-none" data-testid="fel-statement" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <button data-testid="fel-submit" disabled={busy} className="btn-gold w-full py-3 text-xs uppercase tracking-widest disabled:opacity-50">
              {busy ? "Submitting…" : "Submit Fellowship Application"}
            </button>
          </div>
          <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}.fld:focus{outline:none;border-color:hsl(41 40% 54%)}`}</style>
        </form>
      </section>

      <div className="text-xs text-muted-foreground leading-relaxed max-w-3xl border-t border-border pt-6">
        The Piplight Fellowship is awarded at the sole discretion of Piplight Capital and is subject
        to annual budget approval and satisfactory academic progress. A priority interview does not
        constitute a guarantee of employment. Full program terms, current award cycle deadlines, and
        G7 university eligibility lists are available upon request.
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="kicker text-[10px] block mb-2">{label}</span>{children}</label>
);
